import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Box from "./BoxComponent";
import CustomButton from "./CustomButton";
import lootka from "../assets/images/newLootkaFont.webp";
import { ReactComponent as Close } from "../assets/icons/x-circle.svg";
import RegisterChoise from "./RegisterChoise";
import useUserStore from "../store/userStore";
import {
  AuthApiError,
  otpLogin,
  passwordLogin,
  requestOtp,
  resetPasswordWithOtp,
} from "../services/authService";

interface LoginProps {
  onClose?: () => void;
  switchToSignup?: () => void;
}

type LoginMode = "password" | "otp" | "reset";

const MobileInput = ({ value, onChange, disabled }: { value: string; onChange: (value: string) => void; disabled?: boolean }) => (
  <input
    value={value}
    onChange={(event) => onChange(event.target.value)}
    type="tel"
    inputMode="tel"
    autoComplete="tel"
    disabled={disabled}
    placeholder="شماره موبایل"
    className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-right text-sm font-myYekanFaNumRegular outline-none transition focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15 disabled:bg-gray-50"
  />
);

const Login = ({ onClose, switchToSignup }: LoginProps) => {
  const [mode, setMode] = useState<LoginMode>("password");
  const [otpStep, setOtpStep] = useState<"mobile" | "code">("mobile");
  const [mobile, setMobile] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const saveSession = (response: Record<string, any>) => {
    const payload = response.data ?? response;
    const user = payload.user ?? payload;
    const token = payload.session_token ?? response.session_token ?? payload.access_token ?? response.access_token ?? null;
    if (!user?.user_id) throw new Error("پاسخ ورود معتبر نیست.");
    setUser({
      userId: user.user_id,
      username: user.username,
      email: user.email,
      fullName: user.full_name,
      image: user.image ?? null,
    }, typeof token === "string" ? token : null);
  };

  const errorMessage = (error: unknown) => {
    if (error instanceof AuthApiError) {
      if (error.type === "invalid_otp") return "کد واردشده نادرست است یا منقضی شده است.";
      if (error.status === 429 || error.type === "otp_cooldown") return "لطفاً کمی صبر کنید و دوباره تلاش کنید.";
      if (error.type === "mobile_in_use") return "این شماره قبلاً ثبت‌نام شده است. وارد شوید یا رمز را بازیابی کنید.";
      return error.message;
    }
    return "خطایی رخ داد. لطفاً دوباره تلاش کنید.";
  };

  const requestCode = async (purpose: "login" | "password_reset") => {
    if (!mobile.trim()) {
      toast.error("شماره موبایل را وارد کنید.", { className: "toast" });
      return;
    }
    try {
      setIsLoading(true);
      const result = await requestOtp(mobile, purpose);
      setOtpStep("code");
      setResendSeconds(result.data?.resend_after_seconds ?? 60);
      toast.success("اگر این شماره به حساب فعالی متصل باشد، کد ارسال شده است.", { className: "toast" });
    } catch (error) {
      toast.error(errorMessage(error), { className: "toast" });
    } finally {
      setIsLoading(false);
    }
  };

  const submitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      saveSession(await passwordLogin({ username_or_email: username, password }));
      toast.success("با موفقیت وارد شدید.", { className: "toast" });
      onClose?.();
    } catch (error) {
      toast.error(error instanceof AuthApiError && error.status === 401 ? "اطلاعات واردشده صحیح نیست." : errorMessage(error), { className: "toast" });
    } finally {
      setIsLoading(false);
    }
  };

  const submitOtpLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error("کد تایید باید دقیقاً ۶ رقم باشد.", { className: "toast" });
      return;
    }
    try {
      setIsLoading(true);
      saveSession(await otpLogin(mobile, code));
      toast.success("با موفقیت وارد شدید.", { className: "toast" });
      onClose?.();
    } catch (error) {
      toast.error(errorMessage(error), { className: "toast" });
    } finally {
      setIsLoading(false);
    }
  };

  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code) || newPassword.length < 8) {
      toast.error("کد ۶ رقمی و رمز جدید حداقل ۸ کاراکتر است.", { className: "toast" });
      return;
    }
    try {
      setIsLoading(true);
      await resetPasswordWithOtp(mobile, code, newPassword);
      toast.success("رمز عبور تغییر کرد. اکنون وارد شوید.", { className: "toast" });
      setMode("password");
      setOtpStep("mobile");
      setCode("");
      setNewPassword("");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(errorMessage(error), { className: "toast" });
    } finally {
      setIsLoading(false);
    }
  };

  const changeMode = (nextMode: LoginMode) => {
    setMode(nextMode);
    setOtpStep("mobile");
    setCode("");
  };

  const isCodeStep = otpStep === "code";

  return (
    <Box heightClass="min-h-[500px] p-5 relative">
      <button onClick={onClose} className="absolute left-6 top-6 z-50 text-2xl text-gray-700 hover:text-gray-500 desktop:hidden" aria-label="بستن"><Close /></button>
      <img src={lootka} alt="لوتکا" className="absolute top-4 w-32 object-contain tablet:w-40" />
      <RegisterChoise switchToSignup={switchToSignup} active="login" />

      <div className="mt-32 w-full max-w-[320px]">
        <div className="mb-5 grid grid-cols-2 rounded-xl bg-gray-100 p-1 text-xs font-myYekanMedium">
          <button type="button" onClick={() => changeMode("password")} className={`rounded-lg py-2 ${mode === "password" ? "bg-white text-[#038545] shadow-sm" : "text-gray-600"}`}>نام کاربری و رمز</button>
          <button type="button" onClick={() => changeMode("otp")} className={`rounded-lg py-2 ${mode === "otp" ? "bg-white text-[#038545] shadow-sm" : "text-gray-600"}`}>ورود با پیامک</button>
        </div>

        {mode === "password" && (
          <form onSubmit={submitPassword} className="space-y-3">
            <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" placeholder="نام کاربری" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15" />
            <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" placeholder="رمز عبور" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15" />
            <button type="button" onClick={() => changeMode("reset")} className="text-xs text-[#068646] hover:underline">فراموشی یا تغییر رمز عبور</button>
            <CustomButton type="submit" disabled={isLoading} text={isLoading ? "در حال ورود..." : "ورود به لوتکا"} className="bg-[#038545] text-white text-sm w-full rounded-xl justify-center font-myYekanFaNumRegular disabled:opacity-50" />
          </form>
        )}

        {mode === "otp" && (
          <form onSubmit={isCodeStep ? submitOtpLogin : (event) => { event.preventDefault(); void requestCode("login"); }} className="space-y-3">
            <MobileInput value={mobile} onChange={setMobile} disabled={isCodeStep} />
            {isCodeStep && <input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="کد ۶ رقمی" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-center text-lg tracking-[0.35em] outline-none focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15" />}
            {isCodeStep && <button type="button" disabled={resendSeconds > 0 || isLoading} onClick={() => void requestCode("login")} className="text-xs text-[#068646] disabled:text-gray-400">{resendSeconds > 0 ? `ارسال مجدد تا ${resendSeconds} ثانیه` : "ارسال مجدد کد"}</button>}
            <CustomButton type="submit" disabled={isLoading} text={isLoading ? "در حال بررسی..." : isCodeStep ? "تایید و ورود" : "ارسال کد تایید"} className="bg-[#038545] text-white text-sm w-full rounded-xl justify-center disabled:opacity-50" />
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={isCodeStep ? submitReset : (event) => { event.preventDefault(); void requestCode("password_reset"); }} className="space-y-3">
            <p className="text-xs leading-6 text-gray-600">برای تغییر رمز، کد تایید به شماره موبایل شما ارسال می‌شود.</p>
            <MobileInput value={mobile} onChange={setMobile} disabled={isCodeStep} />
            {isCodeStep && <>
              <input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="کد ۶ رقمی" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-center text-lg tracking-[0.35em] outline-none focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15" />
              <input value={newPassword} onChange={(event) => setNewPassword(event.target.value)} type="password" autoComplete="new-password" placeholder="رمز عبور جدید" className="h-11 w-full rounded-xl border border-gray-300 px-3 text-right text-sm outline-none focus:border-[#038545] focus:ring-2 focus:ring-[#038545]/15" />
              <button type="button" disabled={resendSeconds > 0 || isLoading} onClick={() => void requestCode("password_reset")} className="text-xs text-[#068646] disabled:text-gray-400">{resendSeconds > 0 ? `ارسال مجدد تا ${resendSeconds} ثانیه` : "ارسال مجدد کد"}</button>
            </>}
            <CustomButton type="submit" disabled={isLoading} text={isLoading ? "در حال ثبت..." : isCodeStep ? "تغییر رمز عبور" : "ارسال کد تایید"} className="bg-[#038545] text-white text-sm w-full rounded-xl justify-center disabled:opacity-50" />
            <button type="button" onClick={() => changeMode("password")} className="w-full text-xs text-gray-600 hover:text-[#038545]">بازگشت به ورود</button>
          </form>
        )}

        <button type="button" onClick={switchToSignup} className="mt-5 w-full text-sm font-myYekanFaNumRegular">حساب کاربری ندارید؟ ثبت‌نام</button>
      </div>
    </Box>
  );
};

export default Login;
